import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pb-24 pt-48">
      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy
          </h1>
          <p className="text-sm mb-12 opacity-70">Effective Date: April 2026</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <p>
              At Ricky's Bees, we are committed to protecting your privacy and handling your personal information in a safe and responsible manner. This Privacy Policy outlines how we collect, use, disclose and protect your information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
            </p>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Information We Collect</h2>
              <p className="mb-4">We may collect personal information when you interact with our website, including:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Billing and shipping address</li>
                <li>Payment details (processed securely via third-party providers)</li>
                <li>Purchase history</li>
                <li>Any information you provide via contact forms or customer service</li>
              </ul>
              <p className="mb-4">We may also collect non-personal data such as:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Website usage data (via cookies and analytics tools)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>2. How We Collect Your Information</h2>
              <p className="mb-4">We collect your information when you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Place an order</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us via email or website forms</li>
                <li>Browse our website (via cookies)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Your Information</h2>
              <p className="mb-4">Your information is used to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Process and deliver your orders</li>
                <li>Communicate with you about your purchases</li>
                <li>Provide customer support</li>
                <li>Send marketing emails (only if you opt in)</li>
                <li>Improve our website and customer experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Sharing Your Information</h2>
              <p className="mb-4">We do not sell or rent your personal information.</p>
              <p className="mb-4">We may share your information with trusted third parties, including:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Payment processors (e.g., Stripe, PayPal)</li>
                <li>Shipping providers (e.g., Australia Post, couriers)</li>
                <li>Website hosting and analytics providers</li>
              </ul>
              <p>These providers are only given the information necessary to perform their services.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Cookies and Tracking Technologies</h2>
              <p className="mb-4">Our website uses cookies to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Improve website functionality</li>
                <li>Analyse website traffic</li>
                <li>Personalise your experience</li>
              </ul>
              <p>You can disable cookies through your browser settings if you prefer.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Data Security</h2>
              <p>We take reasonable steps to protect your personal information from misuse, interference, loss and unauthorised access. However, no online system is completely secure.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Access and Correction</h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Request access to your personal information</li>
                <li>Request corrections if your information is inaccurate</li>
              </ul>
              <p>To do so, please contact us using the details below.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Marketing Communications</h2>
              <p className="mb-4">If you have opted in, we may send you promotional emails about new products, offers and updates.</p>
              <p>You can unsubscribe at any time by clicking the "unsubscribe" link in our emails.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Third-Party Links</h2>
              <p>Our website may contain links to third-party websites. We are not responsible for their privacy practices.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Contact Us</h2>
              <p className="mb-4">If you have any questions or concerns about this Privacy Policy, please contact:</p>
              <p className="font-semibold">Ricky's Bees</p>
              <p>Email: <a href="mailto:sales@rickysbees.com.au" className="text-primary hover:underline">sales@rickysbees.com.au</a></p>
              <p>Location: Victoria, Australia</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
